<!-- eslint-disable vue/no-setup-props-destructure -->
<script setup lang="ts">
import { ElEmpty } from 'element-plus'
import { useIntersectionObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { useVisitsStore } from '~/stores/visits'
import VisitRow from '~/components/VisitRow.vue'
import VisitDateHeader from '~/components/VisitDateHeader.vue'
import type { GeoFenceVisitWithObjectsLinked } from '~/services/apiClientGps'
import { getDateHeaderId } from '~/utils/dateTimeUtils'

const visitsStore = useVisitsStore()
const { visits, loading, hasMore, selectedVisit } = storeToRefs(visitsStore)

// Watch for changes in visits array and select first item
watch(
  () => visits.value,
  (newVisits) => {
    if (newVisits.length > 0 && !selectedVisit.value) {
      visitsStore.selectVisit(newVisits[0])
    }
  },
  { immediate: true }
)

// The parent component `tracking.vue` now handles the initial data load
// to prevent a race condition where this component would load data before
// filters from URL parameters were applied. This component now only
// handles loading more visits when the user scrolls.
//
// onMounted(async () => {
//   await visitsStore.loadMore()
// })

// Handle visit selection
const handleVisitClick = (visit: GeoFenceVisitWithObjectsLinked) => {
  visitsStore.selectVisit(visit)
}

// Infinite scroll implementation using intersection observer
const loadingTrigger = ref<HTMLElement | null>(null)
useIntersectionObserver(
  loadingTrigger,
  ([{ isIntersecting }]) => {
    if (isIntersecting && !loading.value && hasMore.value) {
      visitsStore.loadMore()
    }
  },
  {
    threshold: 0.1,
    rootMargin: '100px',
  }
)
</script>

<template>
  <div class="visits-container">
    <ElEmpty
      v-if="!loading && !visits.length"
      description="No visits found!"
      :image-size="200"
    >
      <template #description>
        <p class="text-gray-500">
          No visits match your current search criteria
        </p>
      </template>
    </ElEmpty>

    <div v-else-if="visits.length > 0" class="h-full overflow-auto">
      <template v-for="(visit, index) in visits" :key="visit.id">
        <!-- Add date header if this is a new date -->
        <VisitDateHeader
          v-if="
            index === 0 ||
            getDateHeaderId(visit.arrival) !==
              getDateHeaderId(visits[index - 1].arrival)
          "
          :visit="visit"
          :visit-count="
            visits.filter(
              (v) =>
                getDateHeaderId(v.arrival) === getDateHeaderId(visit.arrival)
            ).length
          "
        />

        <!-- Visit row -->
        <VisitRow
          :visit="visit"
          :is-selected="selectedVisit?.id === visit.id"
          :index="index"
          :total-visits="visits.length"
          @select="handleVisitClick"
        />
      </template>

      <!-- Loading trigger -->
      <div v-if="hasMore" ref="loadingTrigger" class="loading-trigger-wrapper">
        <div
          v-if="loading"
          class="text-gray-500 text-sm flex items-center justify-center py-4"
        >
          <i class="animate-spin mr-2">⏳</i> Loading more visits...
        </div>
        <div
          v-else
          class="text-gray-400 text-xs flex items-center justify-center py-4"
        >
          Scroll to load more...
        </div>
      </div>

      <!-- End of results message -->
      <div v-else-if="visits.length > 0" class="end-indicator">
        <div
          class="text-gray-400 text-xs flex items-center justify-center py-4"
        >
          No more visits to load
        </div>
      </div>
    </div>

    <!-- Initial loading state -->
    <div v-else-if="loading" class="h-full flex items-center justify-center">
      <div class="text-gray-500 text-sm">
        <i class="animate-spin mr-2">⏳</i> Loading visits...
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.visits-container {
  height: 100%;
  background: white;
}

// Styling for list items
.loading-trigger-wrapper {
  background: white;
  border-top: 1px solid #f3f4f6;
  padding: 1rem 0;
}

.end-indicator {
  border-top: 1px solid #e5e7eb;
  background: white;
}
</style>
